import { MostUsedAgentProps } from './MostUsedAgent'
import {
  IconUsers,
  IconBuildingSkyscraper,
  IconUser,
  IconRobotFace,
  IconBrain,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconLock,
  IconHourglassLow
} from '@tabler/icons-react'
import { AgentChatIcon, MonitorIcon } from '../Chatbot/icons/Icons'
// @ts-ignore
import { Badge, Button } from 'dsm-react-lib'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import React, { useEffect, useState } from 'react'
import { DropdownItem, DropdownToggle, UncontrolledDropdown, Card, CardBody } from 'reactstrap'
import { DropdownMenuContainer, ButtonSpinner } from '../../../styles/common'
import { DropdownOption } from '../Applications/styles'
import { getAvatarDisplayUrl, getInitials } from '../../../utility/Utils'
import { getAvailabilityBadgeProps } from '../AgentsAndSkills/agentsSkillsConstants'
import { setSelectedAgent } from '../../../_redux/actions/chatbotActions'
import { useDispatch, useSelector } from 'react-redux'
import DeleteAgentModal from '../Chatbot/modals/DeleteAgentModal'
import ArcRoleSelectionModal from '../AI-marketplace/ArcRoleSelectionModal'
import { useAgentArcService } from '../Chatbot/agent/agentArcService'
import { Role } from '../../../utility/types'

const AgentCards: React.FC<MostUsedAgentProps> = ({
  agentData,
  role,
  onBeforeNavigate,
  setAgentsData
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [hoveredIdx, setHoveredIdx] = React.useState<number | null>(null)
  const [hoveredDotsIdx, setHoveredDotsIdx] = React.useState<number | null>(null)
  const { teamId } = useParams<{ teamId: string }>()
  const dispatch = useDispatch()
  const { roles } = useSelector((state: any) => state.userContextReducer.userContext)
  const isCentraliAdmin = roles?.includes(Role.Admin)
  const [deleteAgent, setDeleteAgent] = useState<any>({ isOpen: false, agent: null })
  const [failedAvatarIds, setFailedAvatarIds] = useState<Set<any>>(new Set())
  const [requestingAgentId, setRequestingAgentId] = useState<any>(null)
  const [arcRoleModal, setArcRoleModal] = useState<{ isOpen: boolean; agent: any }>({
    isOpen: false,
    agent: null
  })
  const { requestAgentArcAccess, getArcRoleOptions } = useAgentArcService()

  const submitArcRequest = async (agent: any, role?: string) => {
    setRequestingAgentId(agent.id)
    await requestAgentArcAccess(agent, role)
    setRequestingAgentId(null)
  }

  const onRequestArcAccess = (agent: any) => {
    const roleOptions = getArcRoleOptions(agent)
    if (roleOptions.length > 1) {
      setArcRoleModal({ isOpen: true, agent })
      return
    }
    submitArcRequest(agent, roleOptions[0]?.role)
  }

  // Keying by agent identity (not array index) avoids a stale failure carrying over to a
  // different agent when the list re-orders, filters, or refreshes. Reset whenever the set
  // of avatar URLs in view changes so a previously-broken avatar gets a fresh attempt.
  const avatarUrlsKey = agentData?.map((agent) => agent?.avatarUrl).join('|')
  useEffect(() => {
    setFailedAvatarIds(new Set())
  }, [avatarUrlsKey])

  return (
    <div className="d-flex max-h-[80vh] overflow-auto pt-2">
      {agentData.map((agent, idx) => {
        const showActions =
          role !== 'Member' &&
          agent?.metadata?.userRole !== 'Member' &&
          (agent.agentType !== 'Enterprise' || isCentraliAdmin)
        const availabilityBadge = getAvailabilityBadgeProps(
          agent?.metadata?.availabilityLabel || agent?.availabilityLabel
        )
        return (
          <Card
            key={idx}
            className="h-full card-bg-transparent !border-none min-w-[350px] min-h-[204px] flex-shrink-0 !shadow-none transition-all duration-300 hover:scale-105 relative overflow-visible z-[1] mb-0"
            onClick={() => {}}>
            <CardBody className="flex !pl-0 pb-1 items-start position-relative overflow-visible">
              <div
                className={`flex flex-col items-center relative theme-surface rounded-lg w-[360px] min-h-[100%] position-relative overflow-visible theme-card-border${
                  hoveredIdx === idx ? ' theme-card-border-hover' : ''
                }`}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}>
                {availabilityBadge && !showActions && (
                  <Badge
                    type={availabilityBadge.type}
                    variant={availabilityBadge.variant}
                    color={availabilityBadge.color}
                    label={availabilityBadge.label}
                    className={`absolute top-3 right-3 z-[3] ${availabilityBadge.className} !my-[0.7rem] !mx-[0.5rem]`}
                  />
                )}
                <div
                  className={`theme-card-icon-border rounded-full w-24 h-24 px-2 py-1 pointer-events-none absolute left-[1.5rem] -top-[3rem] z-[2] flex items-center justify-center`}>
                  {agent.avatarUrl && !failedAvatarIds.has(agent.id) ? (
                    <img
                      src={getAvatarDisplayUrl(agent.avatarUrl)}
                      width="85px"
                      height="85px"
                      alt="avatar"
                      className="rounded-full theme-card-icon-border min-w-[85px]"
                      onError={() => setFailedAvatarIds((prev) => new Set(prev).add(agent.id))}
                    />
                  ) : (
                    <div className="rounded-full min-w-[85px] h-[85px] flex items-center justify-center theme-avatar-placeholder theme-card-icon-border text-[2rem]">
                      <IconRobotFace size={46} className="text-[var(--icon-muted)]" stroke={1.5} />
                    </div>
                  )}
                </div>
                <div className="w-full mt-4 p-2 relative">
                  <h3 className="font-bold theme-text-heading line-clamp-1 ellipsis">
                    {agent.name}
                  </h3>
                  <p className="subtitle-text line-clamp-2 break-words ellipsis min-h-[40px] mb-0">
                    {agent.description}
                  </p>
                  <div className="absolute -top-6 right-4 flex items-center space-x-1">
                    <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                      {/* <MonitorIcon size={24} onClick={() => {}} /> */}
                    </div>
                    {availabilityBadge && showActions && (
                      <Badge
                        type={availabilityBadge.type}
                        variant={availabilityBadge.variant}
                        color={availabilityBadge.color}
                        label={availabilityBadge.label}
                        className={`z-[3] ${availabilityBadge.className}`}
                      />
                    )}
                    {showActions && (
                      <div
                        className="d-flex align-items-center"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <UncontrolledDropdown direction="start">
                          <DropdownToggle
                            tag="span"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            onMouseEnter={() => setHoveredDotsIdx(idx)}
                            onMouseLeave={() => setHoveredDotsIdx(null)}
                            className="cursor-pointer">
                            <IconDotsVertical
                              size={24}
                              stroke={1.8}
                              color={hoveredDotsIdx === idx ? 'var(--color-primary)' : undefined}
                            />
                          </DropdownToggle>
                          <DropdownMenuContainer
                            $positionFixed={true}
                            style={{ right: '0', left: 'auto' }}>
                            <DropdownItem
                              className="w-100 theme-dropdown-accent-item"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                navigate(
                                  location.pathname.includes('team')
                                    ? `/teams/${teamId}/edit-agent/${agent.id}`
                                    : `/agents/edit-agent/${agent.id}`
                                )
                              }}>
                              <DropdownOption className="flex items-center">
                                <IconEdit size={15} stroke={1.5} />
                                <span className="align-middle ms-50">Manage</span>
                              </DropdownOption>
                            </DropdownItem>
                            <DropdownItem
                              className="w-100 theme-dropdown-accent-item"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                navigate(`/agents/${agent.id}/memories`)
                              }}>
                              <DropdownOption className="flex items-center">
                                <IconBrain size={15} stroke={1.5} />
                                <span className="align-middle ms-50">Memories</span>
                              </DropdownOption>
                            </DropdownItem>
                            <DropdownItem
                              className="w-100 theme-dropdown-danger-item"
                              onClick={() => {
                                setDeleteAgent({ isOpen: true, agent })
                              }}>
                              <DropdownOption className="flex items-center">
                                <IconTrash size={15} stroke={1.5} />
                                <span className="align-middle ms-50">Delete</span>
                              </DropdownOption>
                            </DropdownItem>
                          </DropdownMenuContainer>
                        </UncontrolledDropdown>
                      </div>
                    )}
                  </div>
                </div>
                <div className="theme-surface-muted flex flex-col p-7 w-full h-full rounded-b-lg min-h-[104px]">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-5">
                    <div className="flex flex-col items-start">
                      <div className="block w-[120%]">
                        <p className="section-label mb-0">VISIBILITY</p>
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center position-relative">
                            {agent.agentType === 'Team' && (
                              <Badge
                                icon={<IconUsers size={16} color="white" />}
                                label="Team"
                                className="font-semibold"
                                color="var(--team-agent)"
                              />
                            )}
                            {agent.agentType === 'Enterprise' && (
                              <Badge
                                icon={<IconBuildingSkyscraper size={16} color="white" />}
                                label="Enterprise"
                                className="font-semibold"
                                color="var(--enterprise-agent)"
                              />
                            )}
                            {agent.agentType === 'Personal' && (
                              <Badge
                                icon={<IconUser size={16} color="white" />}
                                label="Personal"
                                className="font-semibold"
                                color="var(--color-secondary)"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-start">
                      <p className="section-label mb-0">SOURCE</p>
                      <div>
                        <b className="text-base theme-text-heading">
                          {agent?.metadata?.provider || agent?.source || 'Centrali'}
                        </b>
                      </div>
                    </div>
                  </div>
                  {/* {agent?.skills && (
                  <div className="d-block gap-x-4 gap-y-2 w-full mt-2">
                    <p className="section-label mb-[6px]">SKILLS</p>
                    <div className="flex items-start space-x-2">
                      {agent?.skills?.slice(0, 3).map((skill: any, index: number) => (
                        <Badge
                          type="pill"
                          key={index}
                          label={
                            typeof skill === 'string'
                              ? skill.replace(/-/g, ' ')
                              : skill?.displayName || skill?.name || ''
                          }
                          className="agent-tag"
                        />
                      ))}
                      <span className="inline-block w-7 h-7">
                        {agent?.skills?.length > 3 ? (
                          <Badge
                            className="inline-flex mb-1 w-7 h-7 rounded-full font-semibold caption-text bg-primary/10 text-primary items-center justify-center"
                            label={`+${agent?.skills.length - 3}`}
                            variant="light"
                            color="primary"
                          />
                        ) : (
                          // Empty placeholder to reserve space
                          <span className="inline-block w-7 h-7" />
                        )}
                      </span>
                    </div>
                  </div>
                )} */}
                  {agent?.plugins?.length > 0 && (
                    <div className="d-block mt-1 gap-x-4 gap-y-2 w-full">
                      <p className="section-label mb-[6px]">PLUGINS</p>
                      <div className="flex items-start space-x-2">
                        {agent?.plugins?.map((plugin, index) => (
                          <img
                            key={index}
                            src={plugin?.logo}
                            alt={plugin?.name}
                            width={plugin?.width}
                            height={plugin?.height}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    {agent?.arc?.isInProcess ? (
                      <Button
                        className="mt-2 w-full flex justify-center items-center cursor-not-allowed theme-warning-light-btn"
                        color="warning"
                        label="Pending Access"
                        icon={<IconHourglassLow size={16} />}
                        variant="outline"
                        disabled
                      />
                    ) : (agent?.arc?.arcRoles?.length ?? 0) > 0 ? (
                      <Button
                        className="mt-2 w-full flex justify-center items-center theme-accent-outline-btn"
                        color="primary"
                        label={
                          requestingAgentId === agent.id ? 'Requesting Access' : 'Request Access'
                        }
                        icon={
                          requestingAgentId === agent.id ? (
                            <ButtonSpinner color="var(--color-primary)" />
                          ) : (
                            <IconLock size={16} />
                          )
                        }
                        variant="outline"
                        disabled={requestingAgentId === agent.id}
                        onClick={(e: any) => {
                          e.preventDefault()
                          e.stopPropagation()
                          onRequestArcAccess(agent)
                        }}
                      />
                    ) : (
                      <Button
                        className="mt-2 w-full flex justify-center items-center"
                        icon={<AgentChatIcon size={16} color="white" />}
                        label="Chat with Agent"
                        color="primary"
                        onClick={(e: any) => {
                          e.preventDefault()
                          e.stopPropagation()
                          const goToChat = (enrichedAgent?: any) => {
                            dispatch(setSelectedAgent({ ...(enrichedAgent || agent) }))
                            navigate('/chat', { state: { fromAgentCard: true } })
                          }
                          if (onBeforeNavigate) {
                            // May return void, or an (optionally async) enriched agent to dispatch instead.
                            const result = onBeforeNavigate()
                            if (result && typeof result.then === 'function') {
                              result.then((enrichedAgent: any) => goToChat(enrichedAgent))
                              return
                            }
                            goToChat(result)
                          } else {
                            goToChat()
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        )
      })}

      {deleteAgent.isOpen && (
        <DeleteAgentModal
          isOpen={deleteAgent.isOpen}
          toggle={() => setDeleteAgent({ isOpen: false, agent: null })}
          agent={deleteAgent.agent}
          onDeleted={(agents) => agents && setAgentsData?.(agents)}
        />
      )}
      {arcRoleModal.isOpen && (
        <ArcRoleSelectionModal
          isOpen={arcRoleModal.isOpen}
          toggle={() => setArcRoleModal({ isOpen: false, agent: null })}
          roles={getArcRoleOptions(arcRoleModal.agent)}
          isLoading={requestingAgentId === arcRoleModal.agent?.id}
          onConfirm={(role) => {
            const agent = arcRoleModal.agent
            setArcRoleModal({ isOpen: false, agent: null })
            submitArcRequest(agent, role)
          }}
        />
      )}
    </div>
  )
}
export default AgentCards
